import Loader from "../common/Loader/Loader";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router";
import { logOutUser, selectUserDetails } from "../../components/Login/redux/userSlice";
import { ALERT_TYPES, APP_ROUTES, USER_TYPES } from "../../config/constants";
import { selectAllCountries, selectAllRoles, serviceSelectPropositionRecord } from "../../config/redux/configurationsSlice";
import CustomAlert from "../common/CustomAlert/CustomAlert";
import { ACTIVE_STATUS, AddUpdateUserErrorState, AddUpdateUserInitialState, IN_ACTIVE_STATUS, statusListData } from "./AddUpdateUserInterface";
import AddUpdateUserPage from "./AddUpdateUserPage";
import { createUser, fetchUserList, getUserDetails, selectAllAdditionalServices, selectAllUserRecord, updateUser } from "./Redux/UserManagementSlice";
import { userStyles } from "./Styles/UserManagementStyle";
import UserDetailsDialog from "./UserDetailsDialog/UserDetailsDialog";
import { userManagementInitState, UserManagementState } from "./UserManagementInterface";
import { UserAddButtonAccess, UserManagementCardAccess } from "./Utils/UserManagementAccessUtils";
import {
  clearUserObj,
  createUserDetailsObject,
  createUserObject,
  createUserRequestObject,
  ERROR_CREATE,
  ERROR_UPDATE,
  getMaxRecordPresent,
  NO_MESSAGE_AVAILABLE,
  updateUserRecordData,
  validateForm,
} from "./Utils/UserManagementUtils";
import { isEmptyOrNull } from "../../utils/DataUtils";

const UserManagementPage = () => {
  const classes = userStyles();
  let location = useLocation();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userInfo: any = useSelector(selectUserDetails);

  const userListService: any = useSelector(selectAllUserRecord);

  const countriesDataList = useSelector(selectAllCountries);
  const rolesListData = useSelector(selectAllRoles);

  const serviceTypeList = useSelector(serviceSelectPropositionRecord);
  const additionalServicesList = useSelector(selectAllAdditionalServices);
  const statusList = statusListData;

  const [roleList, setRoleList] = useState<any>([]);
  const [countriesList, setCountriesList] = useState<any>([]);

  const [userState, setUserState] = useState<UserManagementState>(userManagementInitState);
  const {
    currentCountry: { countryId },
  } = useSelector((state: any) => state.appConfig);

  // Add and Update User Dialog
  const [userDialogState, setUserDialogState] = useState({
    isLoading: false,
    openAddUpdateUserWindow: false,
    userObj: AddUpdateUserInitialState,
    errors: AddUpdateUserErrorState,
    isUpdatingUser: false,
    openUserDetailsWindow: false,
  });

  const updateCountryList = useCallback(async () => {
    let newResponse = countriesDataList.map((r: any) => ({ id: r.countryId, name: r.id }));
    setCountriesList(newResponse);
  }, [countriesDataList]);

  useEffect(() => {
    if (countriesDataList.length > 0) {
      updateCountryList();
    }
  }, [countriesDataList, updateCountryList]);

  const fetchUserDataList = useCallback(async (page = userState.page) => {
    dispatch(fetchUserList({ countryId: countryId, page, size: userState.size }));
  }, [dispatch, countryId, userState.size, userState.page]);

  useEffect(() => {
    const getCurrentConfig = async () => {
      let params = new URLSearchParams(location.search);
      if (location.search) {
        let toUpdateUserId = params.get("userId");
        let userDetailId = params.get("userDetailId");
        if (toUpdateUserId || userDetailId) {
          const userId = userDetailId ?? toUpdateUserId;
          setUserDialogState({ ...userDialogState, isLoading: true });
          const { payload }: any = await dispatch(getUserDetails(userId)); // Fetch User Details
          if (!isEmptyOrNull(payload?.id) && !isEmptyOrNull(payload?.firstName)) {
            if (toUpdateUserId) {
              // If Edit User is clicked
              setUserDialogState({
                ...userDialogState,
                isUpdatingUser: true,
                userObj: createUserObject(payload, userDialogState.userObj),
                openAddUpdateUserWindow: true,
                isLoading: false,
                openUserDetailsWindow: false,
              });
            } else if (userDetailId) {
              // If Action Icon is clicked
              setUserDialogState({
                ...userDialogState,
                isUpdatingUser: false,
                userObj: createUserDetailsObject(payload, userDialogState.userObj),
                openAddUpdateUserWindow: false,
                isLoading: false,
                openUserDetailsWindow: true,
              });
            }
          } else {
            CustomAlert(ALERT_TYPES.ERROR, "Error while fetching User Details.");
            setUserDialogState({ ...userDialogState, isLoading: false });
          }
        }
      }
    };
    getCurrentConfig();
    // eslint-disable-next-line
  }, [location, location.search, dispatch]);

  const handleAddUpdateUserDialogClose = useCallback(() => {
    if (userDialogState.openAddUpdateUserWindow) {
      // If Dialog Open
      // Moving back to USER_MANAGEMENT URL if Update User Dialog
      navigate(APP_ROUTES.USER_MANAGEMENT);
    }
    setUserDialogState({
      ...userDialogState,
      openAddUpdateUserWindow: !userDialogState.openAddUpdateUserWindow,
      userObj: clearUserObj(userDialogState.userObj),
      errors: AddUpdateUserErrorState,
      isUpdatingUser: false,
    });
  }, [userDialogState, navigate]);

  const handleUserObjChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event && event.target) {
      const { name, value } = event.target;
      switch (name) {
        case "emailId": {
          let text = "Only valid mail id Allowed";
          // eslint-disable-next-line
          if (value.match(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)) {
            text = "";
          }
          setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, [name]: value }, errors: { ...userDialogState.errors, [name]: text } });
          break;
        }
        case "role": {
          setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, [name]: value, country: "", countryId: [] }, errors: { ...userDialogState.errors, [name]: "" } });
          break;
        }
        case "country": {
          setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, [name]: value, countryId: [] }, errors: { ...userDialogState.errors, [name]: "" } });
          break;
        }
        case "status": {
          let statusValue: string = ACTIVE_STATUS;
          if (userDialogState.userObj.status === ACTIVE_STATUS) {
            statusValue = IN_ACTIVE_STATUS;
          }
          setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, [name]: statusValue }, errors: { ...userDialogState.errors, [name]: "" } });
          break;
        }
        default: {
          setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, [name]: value }, errors: { ...userDialogState.errors, [name]: "" } });
        }
      }
    }
  };

  const handleCountryChange = useCallback(
    (value: Array<any>) => {
      setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, countryId: value, country: "" }, errors: { ...userDialogState.errors, countryId: "" } });
    },
    [userDialogState]
  );

  const handleServiceTypeChange = useCallback(
    (value: Array<any>) => {
      setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, serviceTypeId: value }, errors: { ...userDialogState.errors, serviceTypeId: "" } });
    },
    [userDialogState]
  );

  const handleFeatureChange = useCallback(
    (value: Array<any>) => {
      setUserDialogState({ ...userDialogState, userObj: { ...userDialogState.userObj, featureId: value }, errors: { ...userDialogState.errors, featureId: "" } });
    },
    [userDialogState]
  );

  const handleSubmit = async () => {
    const valiadte: any = validateForm(userDialogState.userObj);
    if (valiadte.isError) {
      setUserDialogState({ ...userDialogState, errors: { ...userDialogState.errors, ...valiadte.newErrors } });
    } else {
      const userRequest: any = createUserRequestObject(userDialogState.userObj);
      const { payload }: any = await dispatch(createUser(userRequest));
      if (!isEmptyOrNull(payload?.id)) {
        CustomAlert(ALERT_TYPES.SUCCESS, `User ${payload.firstName} ${payload.lastName} added successfully`);
        handleAddUpdateUserDialogClose();
        fetchUserDataList();
      } else {
        let errorMessage = ERROR_CREATE;
        if (payload.message && payload.message !== NO_MESSAGE_AVAILABLE) {
          errorMessage = payload.message;
        }
        CustomAlert(ALERT_TYPES.ERROR, errorMessage);
      }
    }
  };

  const handleUpdateUser = async () => {
    const valiadte: any = validateForm(userDialogState.userObj);
    if (valiadte.isError) {
      setUserDialogState({ ...userDialogState, errors: { ...userDialogState.errors, ...valiadte.newErrors } });
    } else {
      const userRequest: any = createUserRequestObject(userDialogState.userObj);
      const { payload }: any = await dispatch(updateUser(userRequest));
      if (payload?.id) {
        CustomAlert(ALERT_TYPES.SUCCESS, `User ${payload.firstName} ${payload.lastName} updated successfully`);
        if (userInfo !== undefined && userInfo.id !== undefined && userInfo.id === payload.id) {
          CustomAlert(ALERT_TYPES.SUCCESS, `Refresh required. Logging out`);
          dispatch(logOutUser());
        } else {
          handleAddUpdateUserDialogClose();
          fetchUserDataList();
        }
      } else {
        let errorMessage = ERROR_UPDATE;
        if (payload.message && payload.message !== NO_MESSAGE_AVAILABLE) {
          errorMessage = payload.message;
        }
        CustomAlert(ALERT_TYPES.ERROR, errorMessage);
      }
    }
  };

  useEffect(() => {
    if (additionalServicesList.length > 0 && userListService?.data) {
      setUserDialogState(prevState => ({
        ...prevState,
        userObj: {
          ...prevState.userObj,
          userDataRecord: updateUserRecordData(userListService.data, countriesList, serviceTypeList, additionalServicesList)
        },
        isLoading: false,
      }));

      setUserState(prevState => ({
        ...prevState,
        maxRecordPresent: getMaxRecordPresent(userListService.data)
      }));
    }
  }, [additionalServicesList, userListService.data, countriesList, serviceTypeList, userState.page]);

  const handleUserDetailDialogClose = useCallback(() => {
    if (userDialogState.openUserDetailsWindow) {
      navigate(APP_ROUTES.USER_MANAGEMENT);
    }
    setUserDialogState({ ...userDialogState, openUserDetailsWindow: !userDialogState.openUserDetailsWindow });
  }, [userDialogState, navigate]);

  const updateRoleListBasedOnRole = useCallback(
    (rolesListData: any) => {
      if (rolesListData && Array.isArray(rolesListData) && rolesListData.length > 0) {
        if (userInfo?.userProfile?.role) {
          switch (userInfo.userProfile.role) {
            case USER_TYPES.MAF_ADMIN: {
              setRoleList(rolesListData);
              break;
            }
            case USER_TYPES.SUPER_ADMIN: {
              const allowedRoles = rolesListData.filter((role: any) => role.id !== USER_TYPES.MAF_ADMIN && role.id !== USER_TYPES.SUPER_ADMIN);
              setRoleList(allowedRoles);
              break;
            }
            default: {
              setRoleList([]);
            }
          }
        }
      }
    },
    [userInfo]
  );

  useEffect(() => {
    updateRoleListBasedOnRole(rolesListData);
  }, [rolesListData, updateRoleListBasedOnRole]);

  const fetchNextRecord = useCallback((event: any, pageNumber: number) => {
    if (pageNumber === userState.page) {
      return;
    }

    setUserDialogState(prevState => ({
      ...prevState,
      userObj: { ...prevState.userObj, userDataRecord: [] }
    }));

    setUserState(prevState => ({
      ...prevState,
      page: pageNumber
    }));
    dispatch(fetchUserList({ countryId: countryId, page: pageNumber, size: userState.size }));
  }, [dispatch, countryId, userState.page, userState.size]);

  useEffect(() => {
    dispatch(fetchUserList({ countryId: countryId, page: 0, size: userState.size }));
  }, [dispatch, countryId, userState.size]);

  return (
    <div className={classes.container}>
      <div className={classes.headerRoot}>
        <div className={classes.header}>
          <span className={classes.heading}>{`Users`}</span>
        </div>
        <UserAddButtonAccess userInfo={userInfo} classes={classes} handleAddUpdateUserDialogClose={handleAddUpdateUserDialogClose} />
      </div>

      {userListService.loading ? (
        <Loader />
      ) : (
        <UserManagementCardAccess
          userInfo={userInfo}
          classes={classes}
          userDataLoading={userListService.loading}
          additionalServicesList={additionalServicesList}
          userDialogState={userDialogState}
          pageSize={userState.size}
          rowCount={userState.maxRecordPresent}
          pageNumber={userState.page}
          fetchNextRecord={fetchNextRecord}
        />
      )}
      {userDialogState.openAddUpdateUserWindow && (
        <AddUpdateUserPage
          open={userDialogState.openAddUpdateUserWindow}
          handleClose={handleAddUpdateUserDialogClose}
          userObj={userDialogState.userObj}
          errors={userDialogState.errors}
          handleUserObjChange={handleUserObjChange}
          handleCountryChange={handleCountryChange}
          handleServiceTypeChange={handleServiceTypeChange}
          handleSubmit={userDialogState.isUpdatingUser ? handleUpdateUser : handleSubmit}
          handleFeatureChange={handleFeatureChange}
          isUpdatingUser={userDialogState.isUpdatingUser}
          countriesList={countriesList}
          serviceTypeList={serviceTypeList}
          rolesList={roleList}
          additionalServicesList={additionalServicesList}
          statusList={statusList}
          userInfo={userInfo}
        />
      )}
      {userDialogState.openUserDetailsWindow && (
        <UserDetailsDialog
          open={userDialogState.openUserDetailsWindow}
          userObj={userDialogState.userObj}
          handleClose={handleUserDetailDialogClose}
          countriesList={countriesList}
          serviceTypeList={serviceTypeList}
          rolesList={roleList}
          additionalServicesList={additionalServicesList}
          statusList={statusList}
          userInfo={userInfo}
        />
      )}
    </div>
  );
};

export default React.memo(UserManagementPage);
